"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion } from "framer-motion";
import {
	Camera,
	ImageUp,
	Loader2,
	RefreshCw,
	Repeat,
	ShieldAlert,
	Zap,
	ZapOff,
	X,
} from "lucide-react";
import QrScanner from "qr-scanner";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useLanguage } from "@/hooks/useLanguage";
import { useIsMobile } from "@/hooks/useIsMobile";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { cn } from "@/lib/utils";

const uiLog = createLogAdapter("components/shared/scanner-modal");

interface ScannerModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onScan: (data: string) => void;
}

type ScannerState =
	| "idle"
	| "starting"
	| "active"
	| "stopped"
	| "error"
	| "file_scanning";

type ScannerErrorType =
	| "camera_permission"
	| "camera_start"
	| "camera_stalled"
	| "file_decode"
	| "file_invalid"
	| null;

function isPermissionError(message: string) {
	return /permission|denied|notallowed|security/i.test(message);
}

function isVirtualCamera(label: string) {
	return /virtual|obs|snap camera|camo|epoccam|droidcam/i.test(label);
}

type RuntimeBarcodeDetector = {
	detect: (source: ImageBitmapSource) => Promise<Array<{ rawValue?: string }>>;
};

type RuntimeBarcodeDetectorCtor = {
	new (options?: { formats: string[] }): RuntimeBarcodeDetector;
	getSupportedFormats?: () => Promise<string[]>;
};

type RuntimeImageCapture = {
	grabFrame: () => Promise<ImageBitmap>;
};

type RuntimeImageCaptureCtor = new (track: MediaStreamTrack) => RuntimeImageCapture;

const DESKTOP_CAMERA_STORAGE_KEY = "scanner_desktop_camera_id";

async function supportsQrBarcodeDetection() {
	const barcodeDetectorCtor = (globalThis as { BarcodeDetector?: RuntimeBarcodeDetectorCtor })
		.BarcodeDetector;

	if (typeof window === "undefined" || !barcodeDetectorCtor) {
		return false;
	}

	try {
		const formats = typeof barcodeDetectorCtor.getSupportedFormats === "function"
			? await barcodeDetectorCtor.getSupportedFormats()
			: ["qr_code"];
		return formats.includes("qr_code");
	} catch (error) {
		uiLog.warn("Failed to query BarcodeDetector formats", error);
		return false;
	}
}

async function scanFileWithBarcodeDetector(file: File) {
	if (!(await supportsQrBarcodeDetection())) {
		throw new Error("BarcodeDetector unavailable");
	}

	const barcodeDetectorCtor = (globalThis as { BarcodeDetector?: RuntimeBarcodeDetectorCtor })
		.BarcodeDetector;
	if (!barcodeDetectorCtor) {
		throw new Error("BarcodeDetector unavailable");
	}

	const detector = new barcodeDetectorCtor({ formats: ["qr_code"] });
	const bitmap = await createImageBitmap(file);

	try {
		const results = await detector.detect(bitmap);
		const first = results[0];
		if (!first?.rawValue) {
			throw new Error("No QR code found");
		}
		return first.rawValue;
	} finally {
		bitmap.close();
	}
}

async function scanFileWithCanvasRedraw(file: File) {
	const imageBitmap = await createImageBitmap(file);

	try {
		const canvases = [
			{ width: imageBitmap.width, height: imageBitmap.height },
			(() => {
				const maxDimension = 1600;
				if (
					imageBitmap.width <= maxDimension &&
					imageBitmap.height <= maxDimension
				) {
					return null;
				}
				const scale = Math.min(
					maxDimension / imageBitmap.width,
					maxDimension / imageBitmap.height,
				);
				return {
					width: Math.max(1, Math.round(imageBitmap.width * scale)),
					height: Math.max(1, Math.round(imageBitmap.height * scale)),
				};
			})(),
		].filter((value): value is { width: number; height: number } => Boolean(value));

		let lastError: unknown = null;

		for (const size of canvases) {
			const canvas = document.createElement("canvas");
			canvas.width = size.width;
			canvas.height = size.height;

			const context = canvas.getContext("2d");
			if (!context) {
				throw new Error("Unable to get canvas 2d context");
			}

			context.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height);

			try {
				const result = await QrScanner.scanImage(canvas, {
					alsoTryWithoutScanRegion: true,
					returnDetailedScanResult: true,
				});
				return result.data;
			} catch (error) {
				lastError = error;
				uiLog.warn("Canvas redraw scan failed", {
					canvasWidth: canvas.width,
					canvasHeight: canvas.height,
					error,
				});
			}
		}

		throw lastError instanceof Error ? lastError : new Error("Canvas redraw scan failed");
	} finally {
		imageBitmap.close();
	}
}

export default function ScannerModal({
	open,
	onOpenChange,
	onScan,
}: ScannerModalProps) {
	const { language } = useLanguage();
	const { isMobile } = useIsMobile();
	const streamRef = useRef<MediaStream | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const latestScanRef = useRef("");
	const startAttemptRef = useRef(0);
	const playbackWatchdogRef = useRef<number | null>(null);
	const scanLoopTimeoutRef = useRef<number | null>(null);
	const scanInFlightRef = useRef(false);
	const modeRef = useRef<"camera" | "file" | "idle">("idle");
	const barcodeSupportRef = useRef<boolean | null>(null);
	const barcodeDetectorRef = useRef<RuntimeBarcodeDetector | null>(null);
	const scanCanvasRef = useRef<HTMLCanvasElement | null>(null);
	const pendingRestartCameraIdRef = useRef<string | null>(null);
	const suppressAutoStartRef = useRef(false);
	const imageCaptureRef = useRef<RuntimeImageCapture | null>(null);
	const disableImageCaptureRef = useRef(false);

	const [scannerState, setScannerState] = useState<ScannerState>("idle");
	const [isTorchOn, setIsTorchOn] = useState(false);
	const [lastError, setLastError] = useState("");
	const [lastErrorType, setLastErrorType] = useState<ScannerErrorType>(null);
	const [availableCameras, setAvailableCameras] = useState<QrScanner.Camera[]>([]);
	const [selectedDesktopCameraId, setSelectedDesktopCameraId] = useState("");
	const [activeDesktopCameraId, setActiveDesktopCameraId] = useState("");
	const [hasManualDesktopCameraSelection, setHasManualDesktopCameraSelection] =
		useState(false);
	const [videoInstanceKey, setVideoInstanceKey] = useState(0);

	const clearPlaybackWatchdog = useCallback(() => {
		if (playbackWatchdogRef.current !== null) {
			window.clearTimeout(playbackWatchdogRef.current);
			playbackWatchdogRef.current = null;
		}
	}, []);

	const clearScanLoop = useCallback(() => {
		if (scanLoopTimeoutRef.current !== null) {
			window.clearTimeout(scanLoopTimeoutRef.current);
			scanLoopTimeoutRef.current = null;
		}
		scanInFlightRef.current = false;
	}, []);

	const readStoredDesktopCameraId = useCallback(() => {
		if (typeof window === "undefined") return "";

		try {
			return window.localStorage.getItem(DESKTOP_CAMERA_STORAGE_KEY) || "";
		} catch {
			return "";
		}
	}, []);

	const persistDesktopCameraId = useCallback((cameraId: string) => {
		if (typeof window === "undefined" || !cameraId) return;

		try {
			window.localStorage.setItem(DESKTOP_CAMERA_STORAGE_KEY, cameraId);
		} catch {
			// Ignore storage failures.
		}
	}, []);

	const cancelPendingStarts = useCallback(() => {
		startAttemptRef.current += 1;
	}, []);

	const setScannerError = useCallback((type: ScannerErrorType, message: string) => {
		setLastErrorType(type);
		setLastError(message);
		setScannerState("error");
	}, []);

	const destroyScanner = useCallback(
		(nextState: ScannerState = "stopped") => {
			clearPlaybackWatchdog();
			clearScanLoop();
			const stream = streamRef.current;
			streamRef.current = null;
			imageCaptureRef.current = null;
			disableImageCaptureRef.current = false;

			if (stream) {
				for (const track of stream.getTracks()) {
					track.stop();
				}
			}

			if (videoRef.current) {
				videoRef.current.srcObject = null;
				videoRef.current.pause();
			}

			setScannerState(nextState);
			setIsTorchOn(false);
		},
		[clearPlaybackWatchdog, clearScanLoop],
	);

	const verifyVideoPlayback = useCallback(async () => {
		if (isMobile) return;

		const video = videoRef.current;
		if (!video) {
			throw new Error("Scanner video element not found");
		}

		const initialTime = video.currentTime;
		if (video.paused) {
			try {
				await video.play();
			} catch (error) {
				uiLog.warn("Video play retry failed", error);
			}
		}

		await new Promise<void>((resolve) => {
			playbackWatchdogRef.current = window.setTimeout(() => {
				playbackWatchdogRef.current = null;
				resolve();
			}, 1200);
		});

		if (video.readyState < 2 || video.currentTime <= initialTime + 0.01) {
			throw new Error("Camera video stalled after start");
		}
	}, [isMobile]);

	const handleDecoded = useCallback(
		(decodedText: string) => {
			if (!decodedText || latestScanRef.current === decodedText) return;

			if (typeof navigator !== "undefined" && navigator.vibrate) {
				navigator.vibrate(20);
			}

			latestScanRef.current = decodedText;
			destroyScanner();
			onScan(decodedText);
			onOpenChange(false);
			window.setTimeout(() => {
				latestScanRef.current = "";
			}, 400);
		},
		[destroyScanner, onOpenChange, onScan],
	);

	const getVideoTrack = useCallback(() => {
		return streamRef.current?.getVideoTracks()[0] ?? null;
	}, []);

	const ensureBarcodeDetector = useCallback(async () => {
		if (barcodeSupportRef.current === false) return null;
		if (barcodeDetectorRef.current) return barcodeDetectorRef.current;

		if (barcodeSupportRef.current === null) {
			barcodeSupportRef.current = await supportsQrBarcodeDetection();
		}

		if (!barcodeSupportRef.current) {
			return null;
		}

		const barcodeDetectorCtor = (globalThis as { BarcodeDetector?: RuntimeBarcodeDetectorCtor })
			.BarcodeDetector;
		if (!barcodeDetectorCtor) {
			barcodeSupportRef.current = false;
			return null;
		}

		barcodeDetectorRef.current = new barcodeDetectorCtor({ formats: ["qr_code"] });
		return barcodeDetectorRef.current;
	}, []);

	const scanCurrentVideoFrame = useCallback(async () => {
		const video = videoRef.current;
		const track = getVideoTrack();
		const imageCaptureCtor = (globalThis as unknown as { ImageCapture?: RuntimeImageCaptureCtor })
			.ImageCapture;

		let width = video?.videoWidth ?? 0;
		let height = video?.videoHeight ?? 0;
		let sourceBitmap: ImageBitmap | null = null;

		if (!isMobile && track && imageCaptureCtor && !disableImageCaptureRef.current) {
			try {
				if (!imageCaptureRef.current) {
					imageCaptureRef.current = new imageCaptureCtor(track);
				}
				sourceBitmap = await imageCaptureRef.current.grabFrame();
				width = sourceBitmap.width;
				height = sourceBitmap.height;
			} catch (error) {
				disableImageCaptureRef.current = true;
				imageCaptureRef.current = null;
			}
		}

		if ((!width || !height) && video && video.readyState >= 2) {
			width = video.videoWidth;
			height = video.videoHeight;
		}

		if (!width || !height) {
			sourceBitmap?.close();
			return null;
		}

		const scanSize = Math.floor(Math.min(width, height) * 0.62);
		const sourceX = Math.max(0, Math.floor((width - scanSize) / 2));
		const sourceY = Math.max(0, Math.floor((height - scanSize) / 2));
		const targetSize = isMobile ? 360 : 220;

		let canvas = scanCanvasRef.current;
		if (!canvas) {
			canvas = document.createElement("canvas");
			scanCanvasRef.current = canvas;
		}

		if (canvas.width !== targetSize || canvas.height !== targetSize) {
			canvas.width = targetSize;
			canvas.height = targetSize;
		}

		const context = canvas.getContext("2d", { willReadFrequently: true });
		if (!context) {
			sourceBitmap?.close();
			return null;
		}
		context.drawImage(
			sourceBitmap ?? video!,
			sourceX,
			sourceY,
			scanSize,
			scanSize,
			0,
			0,
			targetSize,
			targetSize,
		);

		const detector = await ensureBarcodeDetector();
		if (detector) {
			try {
				const results = await detector.detect(canvas);
				if (results[0]?.rawValue) {
					sourceBitmap?.close();
					return results[0].rawValue;
				}
			} catch (error) {
				uiLog.warn("BarcodeDetector video scan failed", error);
			}
		}

		if (!isMobile) {
			sourceBitmap?.close();
			return null;
		}

		try {
			const result = await QrScanner.scanImage(canvas, {
				alsoTryWithoutScanRegion: true,
				returnDetailedScanResult: true,
			});
			sourceBitmap?.close();
			return result.data;
		} catch {
			sourceBitmap?.close();
			return null;
		}
	}, [ensureBarcodeDetector, getVideoTrack, isMobile]);

	const startFrameScanLoop = useCallback(
		(startAttemptId: number) => {
			clearScanLoop();

			const scheduleNext = (callback: () => void, delay: number) => {
				const video = videoRef.current;
				const supportsFrameCallback =
					video && typeof video.requestVideoFrameCallback === "function";
				if (supportsFrameCallback) {
					video.requestVideoFrameCallback(() => {
						scanLoopTimeoutRef.current = window.setTimeout(callback, delay);
					});
					return;
				}
				scanLoopTimeoutRef.current = window.setTimeout(callback, delay);
			};

			const loop = async () => {
				if (
					startAttemptId !== startAttemptRef.current ||
					modeRef.current !== "camera" ||
					!open
				) {
					return;
				}

				if (scanInFlightRef.current) {
					scheduleNext(() => void loop(), isMobile ? 180 : 1200);
					return;
				}

				scanInFlightRef.current = true;
				const decodedText = await scanCurrentVideoFrame();
				scanInFlightRef.current = false;
				if (decodedText) {
					handleDecoded(decodedText);
					return;
				}

				scheduleNext(() => void loop(), isMobile ? 180 : 1200);
			};

			scheduleNext(() => void loop(), isMobile ? 180 : 1200);
		},
		[clearScanLoop, handleDecoded, isMobile, open, scanCurrentVideoFrame],
	);

	const loadDesktopCameras = useCallback(async () => {
		try {
			const cameras = await QrScanner.listCameras(true);
			const sorted = [...cameras].sort((a, b) => {
				const aVirtual = isVirtualCamera(a.label || "");
				const bVirtual = isVirtualCamera(b.label || "");
				if (aVirtual !== bVirtual) return aVirtual ? 1 : -1;
				return 0;
			});
			setAvailableCameras(sorted);
			return sorted;
		} catch {
			setAvailableCameras([]);
			return [];
		}
	}, []);

	const startCameraStream = useCallback(
		async (constraints: MediaTrackConstraints) => {
			const video = videoRef.current;
			if (!video) {
				throw new Error("Scanner video element not ready");
			}

			const stream = await navigator.mediaDevices.getUserMedia({
				audio: false,
				video: constraints,
			});
			streamRef.current = stream;
			video.srcObject = stream;
			await new Promise<void>((resolve) => {
				if (video.readyState >= 1) {
					resolve();
					return;
				}
				const handleLoadedMetadata = () => {
					video.removeEventListener("loadedmetadata", handleLoadedMetadata);
					resolve();
				};
				video.addEventListener("loadedmetadata", handleLoadedMetadata);
			});
			await video.play();
		},
		[],
	);

	const startScanner = useCallback(async (overrideDesktopCameraId?: string) => {
		if (modeRef.current === "file") {
			return;
		}
		const startAttemptId = ++startAttemptRef.current;
		setScannerState("starting");
		setLastError("");
		setLastErrorType(null);

		try {
			if (!videoRef.current) {
				throw new Error("Scanner video element not ready");
			}

			destroyScanner("starting");

			const desktopTargetCameraId =
				overrideDesktopCameraId ||
				(hasManualDesktopCameraSelection
					? selectedDesktopCameraId
					: readStoredDesktopCameraId());
			const candidates: MediaTrackConstraints[] = isMobile
				? [{ facingMode: { ideal: "environment" } }, { facingMode: "user" }]
				: desktopTargetCameraId
					? [{ deviceId: { exact: desktopTargetCameraId } }]
					: [
							{
								facingMode: "environment",
								width: { ideal: 1280 },
								height: { ideal: 720 },
							},
							{
								facingMode: "user",
								width: { ideal: 1280 },
								height: { ideal: 720 },
							},
						];

			let lastStartError: unknown = null;

			for (const candidate of candidates) {
				destroyScanner("starting");

				try {
					await startCameraStream(candidate);
					if (isMobile) {
						await verifyVideoPlayback();
					}

					if (
						startAttemptId !== startAttemptRef.current ||
						!open ||
						modeRef.current !== "camera"
					) {
						destroyScanner("stopped");
						return;
					}

					if (!isMobile) {
						const track = getVideoTrack();
						const currentDeviceId = track?.getSettings().deviceId || "";
						setActiveDesktopCameraId(currentDeviceId);
						if (desktopTargetCameraId && currentDeviceId === desktopTargetCameraId) {
							setSelectedDesktopCameraId(currentDeviceId);
							setHasManualDesktopCameraSelection(true);
						}
						void loadDesktopCameras();
					}

					setIsTorchOn(false);
					setScannerState("active");
					startFrameScanLoop(startAttemptId);
					return;
				} catch (error) {
					lastStartError = error;
					uiLog.warn("Scanner start attempt failed", { candidate, error });
					destroyScanner("starting");
				}
			}

			const message =
				lastStartError instanceof Error
					? lastStartError.message
					: "Scanner unavailable";
			const errorType = /stalled/i.test(message)
				? "camera_stalled"
				: isPermissionError(message)
					? "camera_permission"
					: "camera_start";

			setScannerError(errorType, message);
		} catch (error) {
			const message = error instanceof Error ? error.message : "Scanner unavailable";
			uiLog.error("Failed to start scanner", error);
			setScannerError(isPermissionError(message) ? "camera_permission" : "camera_start", message);
		}
	}, [
		destroyScanner,
		getVideoTrack,
		hasManualDesktopCameraSelection,
		isMobile,
		loadDesktopCameras,
		open,
		readStoredDesktopCameraId,
		setScannerError,
			selectedDesktopCameraId,
		startCameraStream,
		startFrameScanLoop,
		verifyVideoPlayback,
	]);

	const handleCycleDesktopCamera = useCallback(() => {
		if (isMobile || availableCameras.length < 2) return;

		const currentId =
			activeDesktopCameraId ||
			selectedDesktopCameraId ||
			getVideoTrack()?.getSettings().deviceId ||
			availableCameras[0]?.id;
		const currentIndex = Math.max(
			0,
			availableCameras.findIndex((camera) => camera.id === currentId),
		);
		const nextCamera = availableCameras[(currentIndex + 1) % availableCameras.length];
		if (!nextCamera) return;

		setHasManualDesktopCameraSelection(true);
		persistDesktopCameraId(nextCamera.id);
		suppressAutoStartRef.current = true;
		setSelectedDesktopCameraId(nextCamera.id);
		modeRef.current = "camera";
		cancelPendingStarts();
		destroyScanner("starting");
		pendingRestartCameraIdRef.current = nextCamera.id;
		setVideoInstanceKey((value) => value + 1);
	}, [
		availableCameras,
		activeDesktopCameraId,
		cancelPendingStarts,
		destroyScanner,
		getVideoTrack,
		isMobile,
		persistDesktopCameraId,
		selectedDesktopCameraId,
	]);

	useEffect(() => {
		if (!open || isMobile || modeRef.current !== "camera") return;
		const pendingCameraId = pendingRestartCameraIdRef.current;
		if (!pendingCameraId) return;
		pendingRestartCameraIdRef.current = null;

		const timer = window.setTimeout(() => {
			void startScanner(pendingCameraId);
		}, 0);

		return () => {
			window.clearTimeout(timer);
		};
	}, [isMobile, open, startScanner, videoInstanceKey]);

	const toggleTorch = async () => {
		const track = getVideoTrack();
		if (!track || scannerState !== "active" || !isMobile) return;

		try {
			const capabilities = typeof track.getCapabilities === "function" ? track.getCapabilities() : {};
			if (!("torch" in capabilities)) {
				return;
			}
			const nextState = !isTorchOn;
			await track.applyConstraints({ advanced: [{ torch: nextState } as never] });
			setIsTorchOn(nextState);
		} catch (error) {
			uiLog.error("Torch toggle failed", error);
		}
	};

	const handleSelectFile = useCallback(
		async (file: File) => {
			if (!file) return;
			if (!file.type.startsWith("image/")) {
				setScannerError(
					"file_invalid",
					language === "zh" ? "请选择图片文件" : "Please select an image file",
				);
				return;
			}

			modeRef.current = "file";
			cancelPendingStarts();
			setScannerState("file_scanning");
			setLastError("");
			setLastErrorType(null);
			destroyScanner("file_scanning");

			try {
				let decodedText = "";

				try {
					decodedText = await scanFileWithBarcodeDetector(file);
				} catch (detectorError) {
					uiLog.warn(
						"BarcodeDetector image scan failed, falling back to qr-scanner",
						detectorError,
					);
					try {
						const result = await QrScanner.scanImage(file, {
							alsoTryWithoutScanRegion: true,
							returnDetailedScanResult: true,
						});
						decodedText = result.data;
					} catch (fileScanError) {
						uiLog.warn("Direct file scan failed, retrying via canvas redraw", fileScanError);
						decodedText = await scanFileWithCanvasRedraw(file);
					}
				}

				if (typeof navigator !== "undefined" && navigator.vibrate) {
					navigator.vibrate(20);
				}
				onScan(decodedText);
				onOpenChange(false);
				modeRef.current = "idle";
			} catch (error) {
				uiLog.error("Image scan failed", error);
				setScannerError(
					"file_decode",
					language === "zh"
						? "图片中未识别到有效二维码"
						: "No QR code was detected in the image",
				);
			}
		},
		[destroyScanner, language, onOpenChange, onScan, setScannerError],
	);

	useEffect(() => {
		if (!open) {
			modeRef.current = "idle";
			suppressAutoStartRef.current = false;
			cancelPendingStarts();
			destroyScanner();
			return;
		}

		if (modeRef.current !== "file") {
			modeRef.current = "camera";
		}

		if (!isMobile) {
			const storedDesktopCameraId = readStoredDesktopCameraId();
			setSelectedDesktopCameraId(storedDesktopCameraId);
			setHasManualDesktopCameraSelection(Boolean(storedDesktopCameraId));
		}

		const timer = window.setTimeout(() => {
			if (suppressAutoStartRef.current) {
				suppressAutoStartRef.current = false;
				return;
			}
			if (modeRef.current !== "camera") return;
			window.requestAnimationFrame(() => {
				if (modeRef.current !== "camera") return;
				window.requestAnimationFrame(() => {
					if (modeRef.current !== "camera") return;
					void startScanner();
				});
			});
		}, isMobile ? 400 : 120);

		return () => {
			window.clearTimeout(timer);
		};
	}, [
		cancelPendingStarts,
		destroyScanner,
		isMobile,
		open,
		readStoredDesktopCameraId,
		startScanner,
	]);

	const errorCopy = (() => {
		switch (lastErrorType) {
			case "camera_permission":
				return {
					title: language === "zh" ? "相机权限被拒绝" : "Camera Permission Denied",
					description:
						lastError ||
						(language === "zh"
							? "请在浏览器设置中允许相机访问后重试"
							: "Allow camera access in your browser settings and retry"),
				};
			case "camera_stalled":
				return {
					title: language === "zh" ? "相机画面未正常播放" : "Camera Playback Stalled",
					description:
						lastError ||
						(language === "zh"
							? "浏览器拿到了相机流，但视频未持续播放，请重试"
							: "The camera stream started, but video playback stalled. Retry and try again."),
				};
			case "file_invalid":
				return {
					title: language === "zh" ? "文件类型不支持" : "Unsupported File Type",
					description:
						lastError ||
						(language === "zh"
							? "请选择 PNG、JPG、WEBP 等图片文件"
							: "Select an image file such as PNG, JPG, or WEBP"),
				};
			case "file_decode":
				return {
					title: language === "zh" ? "图片识别失败" : "Image Decode Failed",
					description:
						lastError ||
						(language === "zh"
							? "图片中未识别到有效二维码，请换一张更清晰的图片"
							: "No valid QR code was detected. Try a clearer image."),
				};
			case "camera_start":
			default:
				return {
					title: language === "zh" ? "无法访问相机" : "Camera Access Failed",
					description:
						lastError ||
						(language === "zh"
							? "请确保已授予浏览器相机访问权限"
							: "Please ensure camera permissions are granted"),
				};
		}
	})();

	const renderContent = () => (
		<div className="relative flex h-full w-full flex-col overflow-hidden bg-background">
			<DrawerTitle className="sr-only">Scanner</DrawerTitle>

			<div className="absolute top-0 z-50 flex w-full items-center justify-between px-6 py-8 md:py-6">
				<button
					type="button"
					onClick={() => onOpenChange(false)}
					className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/80 text-foreground backdrop-blur-md transition-transform active:scale-90"
				>
					<X className="h-5 w-5" />
				</button>
				{!isMobile && (
					<span className="text-sm font-medium text-foreground">
						{language === "zh" ? "扫码识别" : "Scan QR Code"}
					</span>
				)}
				{!isMobile && availableCameras.length > 1 ? (
					<button
						type="button"
						onClick={handleCycleDesktopCamera}
						className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary/80 text-foreground backdrop-blur-md transition-transform active:scale-90"
						aria-label={language === "zh" ? "切换摄像头" : "Switch camera"}
					>
						<Repeat className="h-5 w-5" />
					</button>
				) : (
					<div className="w-10" />
				)}
			</div>

			<div
				className={cn(
					"relative flex w-full items-center justify-center overflow-hidden bg-black",
					isMobile ? "flex-1" : "aspect-square",
				)}
			>
				<video
					key={videoInstanceKey}
					ref={videoRef}
					muted
					playsInline
					className="h-full w-full object-cover"
				/>

				<div className="pointer-events-none absolute inset-0 z-10">
					{isMobile ? (
						<div
							className="absolute inset-0 bg-black/40"
							style={{
								maskImage: "radial-gradient(circle, transparent 130px, black 131px)",
								WebkitMaskImage:
									"radial-gradient(circle, transparent 130px, black 131px)",
							}}
						/>
					) : (
						<>
							<div className="absolute left-0 right-0 top-0 h-[calc(50%-130px)] bg-black/40" />
							<div className="absolute bottom-0 left-0 right-0 h-[calc(50%-130px)] bg-black/40" />
							<div className="absolute left-0 top-[calc(50%-130px)] h-[260px] w-[calc(50%-130px)] bg-black/40" />
							<div className="absolute right-0 top-[calc(50%-130px)] h-[260px] w-[calc(50%-130px)] bg-black/40" />
						</>
					)}

					<div className="absolute inset-0 flex items-center justify-center">
						<div className="relative h-[260px] w-[260px] border border-white/20">
							<div className="absolute -left-[2px] -top-[2px] h-6 w-6 rounded-tl-sm border-l-4 border-t-4 border-primary" />
							<div className="absolute -right-[2px] -top-[2px] h-6 w-6 rounded-tr-sm border-r-4 border-t-4 border-primary" />
							<div className="absolute -bottom-[2px] -left-[2px] h-6 w-6 rounded-bl-sm border-b-4 border-l-4 border-primary" />
							<div className="absolute -bottom-[2px] -right-[2px] h-6 w-6 rounded-br-sm border-b-4 border-r-4 border-primary" />

							{scannerState === "active" && (
								<motion.div
									initial={{ top: "0%" }}
									animate={{ top: "100%" }}
									transition={{
										duration: 2.5,
										repeat: Infinity,
										ease: "linear",
									}}
									className="absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent"
								>
									<div className="absolute inset-0 shadow-[0_0_15px_2px_rgba(var(--primary-rgb),0.8)]" />
								</motion.div>
							)}
						</div>
					</div>

					<div
						className={cn(
							"absolute left-0 right-0 text-center",
							isMobile ? "bottom-[20%]" : "bottom-[10%]",
						)}
					>
						<p className="px-6 text-sm font-medium text-white drop-shadow-md">
							{scannerState === "active"
								? language === "zh"
									? "将二维码放入框内，即可自动扫描"
									: "Align QR code within the frame"
								: scannerState === "file_scanning"
									? language === "zh"
										? "正在识别图片..."
										: "Scanning image..."
									: language === "zh"
										? "正在开启相机..."
										: "Starting camera..."}
						</p>
					</div>
				</div>

				<AnimatePresence>
					{(scannerState === "starting" || scannerState === "file_scanning") && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-sm"
						>
							<div className="flex flex-col items-center gap-4">
								<Loader2 className="h-10 w-10 animate-spin text-primary" />
								<p className="text-sm text-muted-foreground">
									{scannerState === "file_scanning"
										? language === "zh"
											? "图片识别中"
											: "Decoding image..."
										: language === "zh"
											? "相机启动中"
											: "Initializing..."}
								</p>
							</div>
						</motion.div>
					)}
					{scannerState === "error" && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="absolute inset-0 z-30 flex items-center justify-center bg-background/80 px-10 text-center backdrop-blur-md"
						>
							<div className="space-y-6">
								<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20 text-red-500">
									<ShieldAlert className="h-8 w-8" />
								</div>
								<div className="space-y-2">
									<h3 className="text-lg font-bold text-foreground">{errorCopy.title}</h3>
									<p className="text-sm text-muted-foreground">{errorCopy.description}</p>
								</div>
								<Button
									variant="outline"
									className="border-primary/20 hover:bg-primary/10"
									onClick={() => {
										modeRef.current = "camera";
										void startScanner();
									}}
								>
									{language === "zh" ? "重试相机" : "Retry Camera"}
								</Button>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			<div
				className={cn(
					"z-50 w-full px-8",
					isMobile ? "bg-background pb-16 pt-4" : "bg-card pb-12 pt-8",
				)}
			>
				<div className="mx-auto flex max-w-xs items-center justify-around">
						<button
							type="button"
							onClick={toggleTorch}
							disabled={scannerState !== "active" || !isMobile}
							className={cn(
								"group flex flex-col items-center gap-2 transition-all active:scale-95 disabled:opacity-30",
								isTorchOn ? "text-primary" : "text-muted-foreground",
						)}
					>
						<div
							className={cn(
								"flex h-14 w-14 items-center justify-center rounded-full transition-all",
								isTorchOn ? "bg-primary/20" : "bg-secondary group-hover:bg-secondary/80",
							)}
						>
							{isTorchOn ? <Zap className="h-6 w-6" /> : <ZapOff className="h-6 w-6" />}
						</div>
						<span className="text-[10px] font-bold uppercase tracking-widest">
							{language === "zh" ? (isTorchOn ? "关闭" : "开灯") : isTorchOn ? "OFF" : "ON"}
						</span>
					</button>

						<button
							type="button"
							onClick={() => {
								modeRef.current = "file";
								cancelPendingStarts();
								fileInputRef.current?.click();
							}}
							className="group flex flex-col items-center gap-2 transition-all active:scale-95 text-muted-foreground"
						>
						<div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary group-hover:bg-secondary/80">
							{scannerState === "file_scanning" ? (
								<Loader2 className="h-6 w-6 animate-spin text-primary" />
							) : (
								<ImageUp className="h-6 w-6" />
							)}
						</div>
						<span className="text-[10px] font-bold uppercase tracking-widest">
							{language === "zh" ? "相册" : "ALBUM"}
						</span>
					</button>

					{!isMobile && (
							<button
								type="button"
								onClick={() => {
									modeRef.current = "camera";
									void startScanner();
								}}
								className="group flex flex-col items-center gap-2 transition-all active:scale-95 text-muted-foreground"
							>
							<div className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary group-hover:bg-secondary/80">
								<RefreshCw className="h-6 w-6" />
							</div>
							<span className="text-[10px] font-bold uppercase tracking-widest">
								{language === "zh" ? "重置" : "RESET"}
							</span>
						</button>
					)}
				</div>
			</div>

			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={(event) => {
					const file = event.target.files?.[0];
					if (file) {
						void handleSelectFile(file);
					} else if (open) {
						modeRef.current = "camera";
						void startScanner();
					}
					event.currentTarget.value = "";
				}}
			/>
		</div>
	);

	if (isMobile) {
		return (
			<Drawer open={open} onOpenChange={onOpenChange}>
				<DrawerContent className="h-[100dvh] border-none bg-background p-0 outline-none">
					{renderContent()}
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-layer-overlay bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content
					className="fixed inset-0 z-layer-panel flex items-center justify-center p-6 outline-none"
				>
					<DialogPrimitive.Title className="sr-only">
						{language === "zh" ? "扫码识别" : "Scan QR Code"}
					</DialogPrimitive.Title>
					<div className="w-full max-w-md overflow-hidden rounded-2xl shadow-2xl border border-border">
						{renderContent()}
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
